import type { ComponentType } from 'react'
import type { UserRole } from '@/entities/session/types'
import {
  BookIcon,
  CertificateIcon,
  GiftIcon,
  LogOutIcon,
  MessageSquareIcon,
  SettingsIcon,
  UserIcon,
  UsersIcon,
} from '@/shared/ui/icons'

export type AccountMenuLinkItem = {
  kind: 'link'
  key: string
  label: string
  to: string
  icon: ComponentType<{ size?: number }>
  /** Bloco do menu: conta (perfil e ajustes) ou ajuda; o menu põe um divisor entre eles. */
  group: 'account' | 'help'
  doctorOnly?: boolean
  adminOnly?: boolean
}

export type AccountMenuLogoutItem = {
  kind: 'logout'
  key: 'logout'
  label: string
  icon: ComponentType<{ size?: number }>
}

export type AccountMenuItem = AccountMenuLinkItem | AccountMenuLogoutItem

export const getAccountMenuItems = (opts: {
  role: UserRole
  isSystemAdmin: boolean
}): AccountMenuItem[] => {
  const { role, isSystemAdmin } = opts
  const isDoctor = role === 'doctor'

  const links: AccountMenuLinkItem[] = [
    { kind: 'link', key: 'profile', label: 'Perfil', to: '/settings/profile', icon: UserIcon, group: 'account' },
    { kind: 'link', key: 'settings', label: 'Configurações', to: '/settings', icon: SettingsIcon, group: 'account' },
    {
      kind: 'link',
      key: 'certificate',
      label: 'Assinatura digital',
      to: '/doctor/settings/certificate',
      icon: CertificateIcon,
      group: 'account',
      doctorOnly: true,
    },
    {
      kind: 'link',
      key: 'admin-users',
      label: 'Usuários e licença',
      to: '/settings/admin-users',
      icon: UsersIcon,
      group: 'account',
      adminOnly: true,
    },
    {
      kind: 'link',
      key: 'documentation',
      label: 'Documentação',
      to: '/documentation?section=inicio',
      icon: BookIcon,
      group: 'help',
    },
    {
      kind: 'link',
      key: 'whats-new',
      label: 'Novidades',
      to: '/documentation/novidades',
      icon: GiftIcon,
      group: 'help',
    },
    {
      kind: 'link',
      key: 'feedback',
      label: 'Enviar feedback',
      to: '/feedback',
      icon: MessageSquareIcon,
      group: 'help',
    },
  ]

  const visibleLinks = links.filter(
    (item) => (!item.doctorOnly || isDoctor) && (!item.adminOnly || isSystemAdmin),
  )

  return [
    ...visibleLinks,
    { kind: 'logout', key: 'logout', label: 'Sair', icon: LogOutIcon },
  ]
}

/** Links do grupo Conta na folha mobile «Mais» (sem Sair — permanece no rodapé da folha). */
export const getAccountMenuMobileLinks = (opts: {
  role: UserRole
  isSystemAdmin: boolean
}): AccountMenuLinkItem[] =>
  getAccountMenuItems(opts).filter((item): item is AccountMenuLinkItem => item.kind === 'link')
